import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DISMISS_KEY = "waitlist-panel-dismissed";
const OPEN_DELAY_MS = 40000;

type FieldErrors = {
  name?: string;
  email?: string;
};

interface WaitlistPanelProps {
  slug: string;
  modelViewerRef?: React.RefObject<HTMLElement>;
  voiceContainerRef?: React.RefObject<HTMLElement>;
}

const WaitlistPanel = ({
  slug,
  modelViewerRef,
  voiceContainerRef,
}: WaitlistPanelProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const { toast } = useToast();

  // Single-fire guard: once any trigger opens the panel (or it's been
  // dismissed/submitted this session), no trigger may reopen it.
  const firedRef = useRef(false);

  const markDismissed = () => {
    firedRef.current = true;
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // sessionStorage unavailable (private mode / disabled) — non-fatal.
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    markDismissed();
  };

  // Arm the four engagement triggers; whichever fires first opens the panel.
  useEffect(() => {
    // Respect a prior dismissal/submit this session — arm nothing.
    let alreadyDismissed = false;
    try {
      alreadyDismissed = sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      alreadyDismissed = false;
    }
    if (alreadyDismissed) {
      firedRef.current = true;
      return;
    }

    const modelEl = modelViewerRef?.current ?? null;
    const voiceEl = voiceContainerRef?.current ?? null;
    let observer: IntersectionObserver | null = null;

    // model-viewer fires camera-change continuously under auto-rotate; only a
    // human drag carries detail.source === "user-interaction".
    const onCameraChange = (e: Event) => {
      const source = (e as CustomEvent<{ source?: string }>).detail?.source;
      if (source === "user-interaction") openPanel();
    };
    const onVoiceClick = () => openPanel();

    function cleanup() {
      window.clearTimeout(timer);
      if (observer) observer.disconnect();
      if (modelEl) modelEl.removeEventListener("camera-change", onCameraChange);
      if (voiceEl) voiceEl.removeEventListener("click", onVoiceClick);
    }

    function openPanel() {
      if (firedRef.current) return;
      firedRef.current = true;
      setOpen(true);
      cleanup();
    }

    // (1) Dwell timer.
    const timer = window.setTimeout(openPanel, OPEN_DELAY_MS);

    if (modelEl) {
      // (2) Scrolled past the model viewer (it has left the top of the viewport).
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && !entry.isIntersecting && entry.boundingClientRect.top < 0) {
          openPanel();
        }
      });
      observer.observe(modelEl);
      // (3) User rotates the 3D model.
      modelEl.addEventListener("camera-change", onCameraChange);
    }

    // (4) User engages with the voice block (delegated click).
    if (voiceEl) voiceEl.addEventListener("click", onVoiceClick);

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes the panel while it's open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email.trim()))
      next.email = "Please enter a valid email address.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "waitlist-signup",
        {
          body: {
            name: name.trim(),
            email: email.trim(),
            source: `tribute/${slug}`,
          },
        }
      );
      if (error) throw error;
      setIsDuplicate(data?.duplicate === true);
      setSubmitted(true);
      markDismissed();
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({
        title: "Could not join the waitlist",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Join the LegacyLens waitlist"
      aria-hidden={!open}
      className={`fixed z-50 inset-x-0 bottom-0 px-4 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[400px] sm:px-0 transition-all duration-300 ease-out ${
        open
          ? "translate-y-0 opacity-100"
          : "translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="relative bg-card/95 backdrop-blur-sm border border-border/50 rounded-2xl shadow-lg p-6 sm:p-7 mb-4 sm:mb-0">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-2xl leading-none text-muted-foreground hover:text-foreground transition-colors"
        >
          ×
        </button>

        {submitted ? (
          <div className="pr-4">
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              {isDuplicate ? "You're already on the list" : "You're on the waitlist"}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We'll email you when LegacyLens opens for families. Until then, you
              can read the{" "}
              <Link
                to="/digital-legacy-directive"
                className="text-primary hover:underline"
              >
                Digital Legacy Directive
              </Link>{" "}
              — the consent framework that governs every tribute we create.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="pr-4">
              <h2 className="font-display text-xl font-semibold text-foreground mb-1">
                Join the waitlist
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                LegacyLens opens for families this fall. We'll let you know when
                we're ready to preserve a parent's or grandparent's legacy.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="waitlist-name">Name</Label>
              <Input
                id="waitlist-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="waitlist-email">Email</Label>
              <Input
                id="waitlist-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Joining..." : "Join the waitlist"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default WaitlistPanel;
